import { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'

type Variant = 'primary' | 'secondary' | 'ghost' | 'outline-light'
type Size = 'md' | 'lg'

interface CommonProps {
  children: ReactNode
  variant?: Variant
  size?: Size
  icon?: boolean
  className?: string
}

const variantClasses: Record<Variant, string> = {
  primary: 'bg-amber-400 text-ink-900 hover:bg-amber-300 shadow-glow-amber',
  secondary: 'bg-ink-900 text-white hover:bg-ink-800',
  ghost: 'bg-transparent text-ink-800 hover:bg-ink-100',
  'outline-light': 'bg-white/0 text-white border border-white/30 hover:bg-white/10',
}

const sizeClasses: Record<Size, string> = {
  md: 'px-5 py-2.5 text-sm',
  lg: 'px-7 py-3.5 text-base',
}

const base = 'inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-all duration-200 active:scale-[0.98]'

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  icon = false,
  className = '',
  href,
  onClick,
  type = 'button',
}: CommonProps & { href?: string; onClick?: () => void; type?: 'button' | 'submit' }) {
  const classes = `${base} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`

  if (href) {
    if (href.startsWith('http') || href.startsWith('mailto') || href.startsWith('tel')) {
      return (
        <a href={href} className={classes} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer">
          {children}
          {icon && <ArrowRight className="h-4 w-4" />}
        </a>
      )
    }
    return (
      <Link to={href} className={classes}>
        {children}
        {icon && <ArrowRight className="h-4 w-4" />}
      </Link>
    )
  }

  return (
    <button type={type} onClick={onClick} className={classes}>
      {children}
      {icon && <ArrowRight className="h-4 w-4" />}
    </button>
  )
}
